import React from 'react';
import styled from 'styled-components';
import { useUser, SignOutButton } from '@clerk/clerk-react';
import { motion } from 'framer-motion';

const AccessDeniedContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  position: relative;
  padding-top: 120px; /* Platz für Navbar */
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 107, 107, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(255, 107, 107, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const AccessDeniedContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 240px); /* Vollständige Zentrierung */
`;

const AccessDeniedCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 550px;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff6b6b, #ee5a52, #ff8a80, #ffcdd2);
    border-radius: 24px 24px 0 0;
  }
`;

const ErrorIcon = styled(motion.div)`
  font-size: 5rem;
  margin-bottom: 2rem;
  filter: drop-shadow(0 4px 8px rgba(255, 107, 107, 0.3));
`;

const ErrorTitle = styled.h1`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 3rem;
  margin: 0 auto 1.5rem auto;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  width: fit-content;
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 1.2rem;
  margin: 0 auto 2.5rem auto;
  line-height: 1.6;
  font-weight: 500;
  text-align: center;
  max-width: 400px;
`;

const UserInfo = styled.div`
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(238, 90, 82, 0.1) 100%);
  padding: 1.5rem 2rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 107, 107, 0.2);
  backdrop-filter: blur(10px);
  margin: 2rem 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 107, 107, 0.05) 0%, rgba(238, 90, 82, 0.05) 100%);
    pointer-events: none;
  }
`;

const UserDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
`;

const UserAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.4rem;
  box-shadow: 0 8px 20px rgba(255, 107, 107, 0.3);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: linear-gradient(135deg, #ff6b6b, #ee5a52, #ff8a80);
    z-index: -1;
    opacity: 0.3;
  }
`;

const UserText = styled.div`
  h3 {
    margin: 0;
    color: ${props => props.theme.colors.text};
    font-size: 1.2rem;
    font-weight: 700;
  }
  p {
    margin: 0;
    color: ${props => props.theme.colors.textLight};
    font-size: 0.95rem;
    font-weight: 500;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
  width: 100%;
`;

const ActionButton = styled.button`
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const SignOutBtn = styled(ActionButton)`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  
  &:hover {
    box-shadow: 0 8px 25px rgba(255, 107, 107, 0.4);
    background: linear-gradient(135deg, #ff5252 0%, #e53935 100%);
  }
`;

const HomeBtn = styled(ActionButton)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  
  &:hover {
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  }
`;

const AdminAccessDenied = () => {
  const { user } = useUser();
  
  const getUserInitials = (user) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.emailAddresses[0]?.emailAddress[0]?.toUpperCase() || 'A';
  };

  return (
    <AccessDeniedContainer>
      <AccessDeniedContent>
        <AccessDeniedCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <ErrorIcon
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: "spring", stiffness: 200 }}
          >
            🚫
          </ErrorIcon>
          <ErrorTitle>Zugriff nicht erlaubt</ErrorTitle>
          <ErrorMessage>
            Sie haben keine Berechtigung, auf den Admin-Bereich zuzugreifen. 
            Nur Benutzer mit Administrator-Rechten können dieses Dashboard verwenden.
          </ErrorMessage>

          <UserInfo>
            <UserDetails>
              <UserAvatar>
                {getUserInitials(user)}
              </UserAvatar>
              <UserText>
                <h3>{user.firstName} {user.lastName}</h3>
                <p>{user.emailAddresses[0]?.emailAddress}</p>
              </UserText>
            </UserDetails>
            <p style={{ margin: 0, color: '#e74c3c', fontSize: '0.9rem' }}>
              Aktuelle Rolle: {user.publicMetadata?.role || 'Keine Rolle zugewiesen'}
            </p>
          </UserInfo>

          <ButtonGroup>
            <SignOutButton>
              <SignOutBtn>Abmelden</SignOutBtn>
            </SignOutButton>
            <HomeBtn onClick={() => window.location.href = '/'}>
              Zur Startseite
            </HomeBtn>
          </ButtonGroup>
        </AccessDeniedCard>
      </AccessDeniedContent>
    </AccessDeniedContainer>
  );
};

export default AdminAccessDenied;
